<template>
    <RbacDisableGuard
        v-slot="{ isDisabledByRbac }"
        :features="[RbacFeature.actionsResolveEntityAction]"
    >
        <div class="DocumentDataReview">
            <LogoLoader v-if="isLoading" />
            <template v-else>
                <DocumentActionHeader
                    ref="actionHeader"
                    title="Document Data Review"
                    subtitle="
                        Check the AI-extracted fields, fill in any missing details,
                        and confirm everything is correct before accepting.
                    "
                    :actions-disabled="isDisabledByRbac"
                    @back="onCancelClick"
                    @secondary-action="onRejectClick"
                    @primary-action="onAcceptClick"
                />

                <DocumentDataReviewEntitySummary
                    ref="entitySummary"
                    :entity="ddrDataStatus.entity"
                    :custom-properties="customPropertiesStatus.list"
                    :document-data-review-id="actionDetails.documentDataReviewId"
                    :show-errors="submitAttempted"
                />

                <DocumentActionSplitLayout
                    splitter-storage-key="document-data-review-splitter-position"
                    :right-panel-min-height="requirementsMinHeight"
                >
                    <template #before>
                        <TrackedActionReviewDocuments
                            :documents="ddrDocumentsStatus.documents || []"
                            :storage-id="actionDetails.documentDataReviewId"
                            :anchor-target="documentsAnchorTarget"
                            :page-top-offset="pageTopOffset"
                            :anchor-gap="documentsAnchorGap"
                            @position-change="documentsFixedTop = $event"
                        />
                    </template>

                    <template #after>
                        <DdrRequirements
                            :ddr-id="actionDetails.documentDataReviewId"
                            :requirements-data="ddrDataStatus.instances"
                            :requirements-schema="requirementsSchemaStatus.data"
                            :rbac-disabled="isDisabledByRbac"
                        />
                    </template>
                </DocumentActionSplitLayout>

                <ActionReviewRejectionModal
                    v-if="showRejectConfirmation"
                    @close="onRejectConfirmation"
                    @input="onRejectConfirmation"
                />
            </template>
        </div>
    </RbacDisableGuard>
</template>

<style lang="scss">
.DocumentDataReview {
    display: flex;
    flex-direction: column;
    background: var(--eidBgGrayTones-lightest);

    .LogoLoader {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 0.7em;
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { Vue } from '@evidentid/vue-property-decorator';

    import LogoLoader from '@evidentid/dashboard-commons/components/LogoLoader/LogoLoader.vue';
    import RbacDisableGuard from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisableGuard.vue';
    import { typeNarrowing } from '@evidentid/dashboard-commons/utils/typeNarrowing';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { DocumentDataReviewRequirementInstance } from '@evidentid/tprm-portal-lib/models/document-data-review';
    import { DocumentDataReviewActionResolveInput } from '@evidentid/tprm-portal-lib/models/entity-actions-review';
    import type { DocumentDataReviewActionDetails } from '@evidentid/tprm-portal-lib/models/entity-actions-review/actions/document-data-review/DocumentDataReviewActionDetails.model';
    import { ActionType } from '@evidentid/tprm-portal-lib/models/entity-actions-review/commons';
    import { ActionReview } from '@evidentid/tprm-portal-lib/models/entity-actions-review/commons/ActionReview.model';
    import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details/TprmRequirementType';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import TrackedActionReviewDocuments
        from '@/components/action-review-documents/TrackedActionReviewDocuments.vue';
    import DdrRequirements from '@/modules/document-data-review/components/commons/ddr-requirements/DdrRequirements.vue';
    import DocumentDataReviewEntitySummary from '@/modules/document-data-review/components/commons/document-data-review/sub-components/DocumentDataReviewEntitySummary.vue';
    import {
        type DocumentDataReviewDataStatus,
        type DocumentDataReviewDocumentsDataStatus,
        type RequirementsSchemaStatus,
        useDocumentDataReviewStore,
    } from '@/modules/document-data-review/store';
    import { buildRequirementFormId, getDdrIdFromFormId } from '@/modules/document-data-review/utils/requirement-form-id-utils/requirementFormIdUtils';
    import { useDynamicFormStore } from '@/modules/dynamic-form/store';
    import { useDashboardStore } from '@/modules/dashboard/vuex/dashboard';
    import ActionReviewRejectionModal
        from '@/modules/entity-actions-review/components/ActionReviewRejectionModal/ActionReviewRejectionModal.vue';
    import DocumentActionHeader
        from '@/modules/entity-actions-review/components/DocumentActionHeader/DocumentActionHeader.vue';
    import DocumentActionSplitLayout
        from '@/modules/entity-actions-review/components/DocumentActionSplitLayout/DocumentActionSplitLayout.vue';

    /** Top offset: 15px page padding + 92px title. Keeps documents panel below header when sticky. */
    const PAGE_TOP_OFFSET_PX = 15 + 92;
    const DOCUMENTS_ANCHOR_GAP_PX = 21;

    export default defineComponent({
        name: 'DocumentDataReview',
        components: {
            ActionReviewRejectionModal,
            DdrRequirements,
            DocumentActionHeader,
            DocumentActionSplitLayout,
            DocumentDataReviewEntitySummary,
            LogoLoader,
            RbacDisableGuard,
            TrackedActionReviewDocuments,
        },
        props: {
            actionReview: {
                type: Object as PropType<ActionReview>,
                required: true,
            },
        },
        // PRODUCT-26110 Task 13: this component's template root is RbacDisableGuard,
        // whose render() always returns a Fragment (see RbacDisableGuard.vue's comment).
        // ActionReviewDetails.vue's `<component :is="reviewComp" @resolve=... @cancel=...>`
        // binds both listeners on every COMPONENT_MAP entry, including this one; without a
        // declared `emits`, Vue 3 treats them as extraneous and warns when it can't forward
        // them through the Fragment root -- declaring them here marks them consumed at this
        // hop instead.
        emits: [ 'resolve', 'cancel' ],
        data() {
            return {
                documentsAnchorTarget: null as HTMLElement | null,
                RbacFeature,
                submitAttempted: false,
                showRejectConfirmation: false,
                documentsFixedTop: PAGE_TOP_OFFSET_PX,
            };
        },
        computed: {
            actionDetails(): DocumentDataReviewActionDetails {
                return typeNarrowing<DocumentDataReviewActionDetails>(
                    (this as any).actionReview.action,
                    (this as any).actionReview.action.$action === ActionType.documentDataReview,
                    'The actionReview object of this component has incorrect data, this is likely caused by mistake in the code.',
                );
            },
            rpName(): string {
                return (this as any).$rp?.current || '';
            },
            isLoading(): boolean {
                return (this as any).loadingCustomProperties
                    || (this as any).loadingDdrData
                    || (this as any).loadingDocuments
                    || (this as any).loadingRequirementsSchema;
            },
            loadingCustomProperties(): boolean {
                return (this as any).customPropertiesStatus.status === OperationStatus.loading;
            },
            loadingDdrData(): boolean {
                return (this as any).ddrDataStatus.status === OperationStatus.loading;
            },
            loadingRequirementsSchema(): boolean {
                return (this as any).requirementsSchemaStatus.status === OperationStatus.loading;
            },
            customPropertiesStatus(): { status: OperationStatus, list: CustomProperty[] } {
                return useDashboardStore().customProperties[(this as any).rpName] || {
                    status: OperationStatus.uninitialized,
                    list: [],
                };
            },
            requirementsSchemaStatus(): RequirementsSchemaStatus {
                return useDocumentDataReviewStore().requirementsSchema[(this as any).rpName] || {
                    status: OperationStatus.uninitialized,
                    data: {},
                };
            },
            ddrDataStatus(): DocumentDataReviewDataStatus {
                const entityId = (this as any).actionReview.insuredId || '';
                const data = useDocumentDataReviewStore().ddrData[entityId];
                return data || {
                    status: OperationStatus.uninitialized,
                    instances: [],
                    entity: null,
                };
            },
            ddrDocumentsStatus(): DocumentDataReviewDocumentsDataStatus {
                const entityId = (this as any).actionReview.insuredId || '';
                return useDocumentDataReviewStore().ddrDocuments[entityId] || {
                    status: OperationStatus.uninitialized,
                    documents: null,
                };
            },
            loadingDocuments(): boolean {
                return (this as any).ddrDocumentsStatus.status === OperationStatus.loading;
            },
            documentsAnchorGap(): number {
                return DOCUMENTS_ANCHOR_GAP_PX;
            },
            pageTopOffset(): number {
                return PAGE_TOP_OFFSET_PX;
            },
            requirementsMinHeight(): string {
                const bottomPadding = 21;
                return `calc(100vh - ${(this as any).documentsFixedTop}px - ${bottomPadding}px)`;
            },
        },
        watch: {
            '$rp.current': {
                immediate: true,
                async handler(rpName: string, prevRpName: string) {
                    if (prevRpName) {
                        useDashboardStore().clearCustomPropertiesList({ rpName: prevRpName });
                    }
                    (this as any).loadCustomProperties();
                },
            },
            'actionReview.insuredId': {
                immediate: true,
                async handler(insuredId: string) {
                    if (insuredId) {
                        (this as any).loadDocumentDataReviewDocuments();
                        await (this as any).loadDocumentDataReviewData();
                        (this as any).loadRequirementsSchema(
                            (this as any).ddrDataStatus.instances?.map(
                                (data: any) => data.requirement,
                            ),
                        );
                    }
                },
            },
            'isLoading': {
                handler(newVal: boolean, oldVal: boolean) {
                    if (oldVal === undefined || (oldVal && !newVal) || !newVal) {
                        this.$nextTick(() => {
                            (this as any).syncDocumentsAnchorTarget();
                            const anchorTarget = (this as any).documentsAnchorTarget;
                            if (anchorTarget) {
                                (this as any).documentsFixedTop = Math.max(
                                    anchorTarget.getBoundingClientRect().bottom + (this as any).documentsAnchorGap,
                                    (this as any).pageTopOffset,
                                );
                            }
                        });
                    }
                },
                immediate: true,
            },
        },
        methods: {
            syncDocumentsAnchorTarget(): void {
                const entitySummary = ((this.$refs.entitySummary as Vue | undefined)?.$el as HTMLElement | undefined);
                const actionHeader = ((this.$refs.actionHeader as Vue | undefined)?.$el as HTMLElement | undefined);
                (this as any).documentsAnchorTarget = entitySummary || actionHeader || null;
            },
            loadCustomProperties(): void {
                if (!(this as any).loadingCustomProperties) {
                    useDashboardStore().loadCustomProperties({ rpName: (this as any).rpName });
                }
            },
            async loadDocumentDataReviewData(): Promise<void> {
                const entityId = (this as any).actionReview.insuredId;
                const documentDataReviewId = (this as any).actionDetails.documentDataReviewId;
                if (!(this as any).loadingDdrData && entityId && documentDataReviewId) {
                    await useDocumentDataReviewStore().loadDocumentDataReviewData(
                        { rpName: (this as any).rpName, entityId, documentDataReviewId },
                    );
                }
            },
            loadRequirementsSchema(requirements: TprmRequirementType[]): void {
                if (!(this as any).loadingRequirementsSchema) {
                    useDocumentDataReviewStore().loadRequirementsSchema(
                        { rpName: (this as any).rpName, requirements },
                    );
                }
            },
            loadDocumentDataReviewDocuments(): void {
                const entityId = (this as any).actionReview.insuredId;
                const documentDataReviewId = (this as any).actionDetails.documentDataReviewId;
                const commonName = (this as any).rpName;
                if (!(this as any).loadingDocuments && entityId && documentDataReviewId && commonName) {
                    useDocumentDataReviewStore().loadDocumentDataReviewDocuments({
                        entityId,
                        documentDataReviewId,
                        commonName,
                    });
                }
            },
            onAcceptClick(): void {
                const formIds = Object.keys(useDynamicFormStore().forms)
                    .filter(
                        (formId) =>
                            getDdrIdFromFormId(formId) === (this as any).actionDetails.documentDataReviewId,
                    );

                if (!this.submitAttempted) {
                    this.submitAttempted = true;
                    if (formIds.length > 0) {
                        useDynamicFormStore().setShowErrorsMultiple({
                            formIds,
                            showError: true,
                        });
                    }
                }

                // Check if there are any form errors
                const hasErrors = formIds.some((formId) => {
                    const form = useDynamicFormStore().forms[formId];
                    return form && Object.keys(form.formErrors || {}).length > 0;
                });

                if (hasErrors) {
                    return;
                }

                // Build the accept data with form values mapped to fields
                const data: DocumentDataReviewActionResolveInput = {
                    $action: ActionType.documentDataReview,
                    data: (this as any).ddrDataStatus.instances.map(
                        (requirementData: any) => {
                            const formId = buildRequirementFormId(
                                (this as any).actionDetails.documentDataReviewId,
                                requirementData.requirement,
                            );
                            const fields = useDynamicFormStore().forms[formId]?.values || {};

                            const hasNonNullFields =
                                Object.values(fields).some((v) => v !== null && v !== undefined);

                            return {
                                requirement: requirementData.requirement,
                                instanceId: requirementData.instanceId || null,
                                fields:
                                    Object.keys(fields).length > 0 && hasNonNullFields
                                        ? fields
                                        : null,
                            };
                        },
                    ),
                };
                (this as any).$emit('resolve', data);
            },
            onRejectClick(): void {
                (this as any).showRejectConfirmation = true;
            },
            onRejectConfirmation(confirmed: boolean): void {
                (this as any).showRejectConfirmation = false;
                if (confirmed) {
                    const data: DocumentDataReviewActionResolveInput = {
                        $action: ActionType.documentDataReview,
                        data: (this as any).ddrDataStatus.instances.map(
                            (requirementData: DocumentDataReviewRequirementInstance) => ({
                                requirement: requirementData.requirement,
                                instanceId: requirementData.instanceId,
                                fields: null,
                            }),
                        ),
                    };
                    (this as any).$emit('resolve', data);
                }
            },
            onCancelClick(): void {
                (this as any).$emit('cancel');
            },
        },
    });
</script>
